import React, { useContext, useEffect } from "react";
import styled, { css } from "styled-components";

import { GlobalContext } from "@/contexts";
import { IThemeProps } from "@/styles";
import { moveDownAndFadeIn, moveUpAndFadeOut } from "@/styles/Mixins";
import Svg from "@/styles/Svg";

export type SublineProps = {
  dismiss?: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  children?: React.ReactNode;
  overlay?: boolean;
};

export const Subline: React.FC<SublineProps> = (
  props: SublineProps
): JSX.Element => {
  const globalContext = useContext(GlobalContext);
  const { showCloseButton, dismiss, children, onClose, overlay } = props;

  useEffect(() => {
    if (onClose && dismiss) {
      setTimeout(onClose, 150);
    }
  }, [dismiss]);

  const className = dismiss ? "fadeOut" : "fadeIn";

  return (
    <Flexbox overlay={overlay} className={overlay ? className : ""}>
      <Content>
        <Headline>{children}</Headline>
        {showCloseButton && (
          <CloseButton onClick={onClose}>
            <Svg
              contents={globalContext.icons.CloseLite}
              height={10}
              width={10}
            />
          </CloseButton>
        )}
      </Content>
    </Flexbox>
  );
};

const SublineHeight = "72px";

const CloseButton = styled.div<IThemeProps>`
  color: ${(props: IThemeProps) => props.theme.colors.fontTextLight};
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  padding-top: 5px;
  cursor: pointer;
`;

const OverlayCss = css`
  position: absolute;
  top: 0;
  z-index: ${(props: IThemeProps) => props.theme.zIndex.h200};
`;

type FlexboxProps = IThemeProps<{ overlay?: boolean }>;
// postcss-styled-components-disable-next-line
const Flexbox = styled.div<FlexboxProps>`
  ${({ overlay }: FlexboxProps) => overlay && OverlayCss}
  display: flex;
  padding: 0;
  width: 100%;
  height: ${SublineHeight};
  ${(props: IThemeProps) =>
    props.theme.borders.interaction && `border: 4px solid transparent;`}

  &.fadeIn {
    animation: ${moveDownAndFadeIn({ size: SublineHeight })} 250ms linear;
  }

  &.fadeOut {
    opacity: 0;
    transform: translateY(-${SublineHeight}});
    animation: ${moveUpAndFadeOut({ size: SublineHeight })} 150ms linear;
  }

  @media ${(props: IThemeProps) => props.theme.breakpoints.mobile} {
    height: 50px;
  }
`;

const Content = styled.div<IThemeProps>`
  display: flex;
  position: relative;
  padding: 0 6%;
  width: 100%;
  background-color: ${(props: IThemeProps) => props.theme.colors.notification};
  ${(props: IThemeProps) =>
    props.theme.borders.interaction &&
    `border-radius: ${props.theme.borders.interaction};`}
  justify-content: center;
  align-items: center;
`;

const Headline = styled.p`
  font-size: ${(props: IThemeProps) => props.theme.typography.h600};
  text-align: center;
  font-weight: normal;
  line-height: 1.3;
  color: ${(props: IThemeProps) => props.theme.colors.fontTextLight};

  @media ${(props: IThemeProps) => props.theme.breakpoints.mobile} {
    font-size: ${(props: IThemeProps) => props.theme.typography.h300};
  }
`;
